import styled from 'styled-components';
import { COLORS } from '../../../constants/radar';

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;

  .spacer {
    flex: 1;
  }
`;

export const SetInfo = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  font-size: 13px;
  color: #64748B;
  margin: 12px 0 4px;

  strong {
    color: ${COLORS.PRIMARY};
    font-weight: 600;
  }
`;

export const PatternCell = styled.span`
  font-family: 'SFMono-Regular', Menlo, Consolas, monospace;
  font-size: 13px;
  color: ${COLORS.PRIMARY};
  word-break: break-word;
`;

export const MutedText = styled.span`
  color: #94A3B8;
  font-size: 13px;
`;

export const ReportSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0;
`;

export const ReportList = styled.ul`
  margin: 4px 0 12px;
  padding-left: 18px;
  max-height: 200px;
  overflow-y: auto;
  font-size: 13px;

  li {
    margin: 2px 0;
  }
`;

// Overview: one summary card per provider (mirrors the landing-page card hover).
export const ProviderCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover,
  &:focus-visible {
    border-color: ${COLORS.PRIMARY};
    transform: translateY(-2px);
    outline: none;
  }
`;

export const ProviderCardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  & > :last-child {
    grid-column: 1 / -1;
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border-radius: 6px;
  background: ${({ $warning }) => ($warning ? '#FEF3C7' : '#f8fafc')};
  color: ${({ $warning }) => ($warning ? '#92400E' : 'inherit')};
`;

export const StatValue = styled.span`
  font-size: 22px;
  font-weight: 600;
  color: inherit;
`;

export const StatLabel = styled.span`
  font-size: 12px;
  color: #64748B;
`;

export const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: #64748B;
  margin-top: auto;
`;

// Header right side: page actions (same pattern as Reports → Bench Candidates).
export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

// Tabs between the header and the content (same look as Edit Individual).
export const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 0 16px;
  background-color: ${COLORS.BACKGROUND_PRIMARY};
`;

export const TabButton = styled.button`
  padding: 12px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${({ 'data-active': active }) => (active ? COLORS.PRIMARY : COLORS.GRAY)};
  border-bottom: 2px solid ${({ 'data-active': active }) => (active ? COLORS.PRIMARY : 'transparent')};
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${COLORS.PRIMARY};
  }

  /* no box around the clicked tab; keyboard focus still gets a ring */
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: -2px;
    border-radius: 4px;
  }
`;

// History tab: one group per set version, compact lines inside.
export const HistoryGroup = styled.section`
  margin-bottom: 20px;
`;

export const HistoryGroupHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 16px;
  padding: 8px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  color: #64748B;

  strong {
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.PRIMARY};
  }
`;

export const HistoryEntry = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0 8px 4px;
  border-bottom: 1px solid #f1f5f9;
  flex-wrap: wrap;
`;

export const HistoryChange = styled.span`
  font-size: 14px;
  color: #475569;
`;

// Muted delimiter between the job title and what happened to it.
export const HistorySeparator = styled.span`
  color: #CBD5E1;
  margin: 0 -4px;
`;

// Same delimiter inside the import preview list, which has no flex gap.
export const ReportSeparator = styled(HistorySeparator)`
  margin: 0 8px;
`;
